import {configure, getConfiguration, IApiConfig, IGameSettings, IRealityCheckInput, IUIState, on, P2P, send} from '@rlx/feim';
import {RelaxGameClientWrapper} from '@gongxigames/framework/integrations/relax/RelaxGameClientWrapper';
import {IEventManagerWithAsync} from '@gongxigames/framework/core';
import {
    BuyBonusButtonEvents, BuyBonusPopUpEvents,
    FlowModuleEvents, GameRulesEvents,
    GlobalEventsMapForBackwardCompatibility, InfoPageEvents, KeypadControlsEvents,
    LogicEvents, RoundInfo
} from '@gongxigames/framework/slot';
import {RubyPlayGameClientWrapperSettings} from '@gongxigames/framework/integrations/rubyplay/RubyPlayGameClientWrapperSettings';
import {strictUiEmitter} from '@gongxigames/framework/slotV2/globals';

type FinishedRoundData = {
    win: number;
    totalWin: number;
    balanceAfter: number;
};

export class RelaxEventsHandler {
    public static readonly NAME: symbol = Symbol('RelaxEventsHandler');
    public static readonly DEFAULT_CURRENCY: string = 'EUR';

    protected _on = on;
    protected _send = send;
    protected _relaxWrapper: RelaxGameClientWrapper;
    protected _eventManager: IEventManagerWithAsync<GlobalEventsMapForBackwardCompatibility>;
    protected _configuration: IApiConfig;

    protected _isGameRulesOpen = false;
    protected _isInfoPageOpen = false;
    protected _isBuyFeatureInProgress = false;
    protected _paidRoundStated = false;

    constructor(relaxWrapper: RelaxGameClientWrapper, eventManager: IEventManagerWithAsync) {
        this._relaxWrapper = relaxWrapper;
        this._eventManager = eventManager;
        this._configuration = getConfiguration();

        this.setupInitialFEIMConfiguration();
        this.setupRealityCheckCallback();
        this.setupEvents();
        this.setupOnEvents();
        this.setupSendEvents();
        this.setupInitialSoundConfig();
    }

    protected setupInitialFEIMConfiguration(): void {
        configure({
            handleRgPostMessageAPI: true,
            handleFeaturePause: true,
            logLevel: 0
        });
    }

    protected setupRealityCheckCallback(): void {
        P2P.onRealityCheckChoiceRequired((choice: IRealityCheckInput) => {
            this._relaxWrapper.sendRCChoiceOnServer(choice.action);
        });
    }

    protected setupEvents(): void {
        // TODO use only one event (new 'UI.gameRules.opened')
        strictUiEmitter.on('UI.gameRules.opened', this.onGameRulesOpened.bind(this));
        strictUiEmitter.on('UI.gameRules.closed', this.onGameRulesClosed.bind(this));
        this.on(GameRulesEvents.OPENED, this.onGameRulesOpened);
        this.on(GameRulesEvents.CLOSED, this.onGameRulesClosed);
        this.on(InfoPageEvents.OPENED, this.onInfoPageOpened);
        this.on(InfoPageEvents.CLOSED, this.onInfoPageClosed);
        strictUiEmitter.on('UI.infoPage.opened', this.onInfoPageOpened.bind(this));
        strictUiEmitter.on('UI.infoPage.closed', this.onInfoPageClosed.bind(this));
    }

    protected setupOnEvents(): void {
        this._on.exitingGame(() => this.exitGame());
        this._on.freeze(() => this.freezeGame());
        this._on.unfreeze(() => this.unfreezeGame());
        this._on.pauseAutoPlay(() => this.stopAutoPlay());
        this._on.toggleGameHelp(() => this.toggleGameRules());
        this._on.togglePaytable(() => this.togglePaytable());
        this._on.updateSettings((settings) => this.updateSettings(settings));
        this._on.refreshBalance(() => this.refreshBalanceRequest());
    }

    protected on(id: string | symbol, listener: Function, priority?: number): void {
        this._eventManager.on(id, listener.bind(this, id), this, priority);
    }

    protected setupSendEvents(): void {
        this.on('General.Game.SYSTEM_INITIALIZED', this.onGameLoadStarted);
        this.on('General.ResourcesLoading.PROGRESS', this.onGameLoading);
        this.on('General.Game.INITIALIZED', this.onGameLoadCompleted);
        this.on('Autoplay.STARTED', this.onAutoPlayStarted);
        this.on('Autoplay.STOPPED', this.onAutoPlayFinished);
        this.on(LogicEvents.BALANCE_UPDATED, this.onBalanceUpdate);
        this.on(LogicEvents.ROUND_START, this.onPaidRoundStarted);
        this.on(KeypadControlsEvents.TOTAL_BET_CHANGED, this.onUpdateBet);
        this.on(KeypadControlsEvents.TURBO_BUTTON_CLICKED, this.onUpdateTurboSettings);
        strictUiEmitter.on('UI.sounds.switchedTo', this.onUpdateSoundSettings.bind(this), {owner: this});
        this.on(FlowModuleEvents.FEATURE_STARTED, this.onFeatureStarted);
        this.on(FlowModuleEvents.FEATURE_FINISHED, this.onFeatureFinished);

        this.on('General.Game.RESTORED', this.onGameRestored);

        this.on('General.ResourcesLoading.START_LOAD_RESOURCE', this.reportUIState.bind(this, {splashScreenVisible: true}));
        this.on('General.Game.STARTED', this.reportUIState.bind(this, {splashScreenVisible: false}));
        this.on('General.GameHistory.Opened', this.reportUIState.bind(this, {busy: true}));
        this.on('General.GameHistory.Closed', this.reportUIState.bind(this, {busy: false}));
        strictUiEmitter.on('UI.gameHistory.opened', this.reportUIState.bind(this, {busy: true}));
        strictUiEmitter.on('UI.gameHistory.closed', this.reportUIState.bind(this, {busy: false}));
        this.on('General.Popups.Add', this.reportUIState.bind(this, {busy: true}));
        this.on('General.Popups.Remove', this.reportUIState.bind(this, {busy: false}));
        this.on(BuyBonusButtonEvents.BUY_BONUS_BUTTON_CLICKED, this.reportUIState.bind(this, {buyFeatureMenuOpen: true}));
        this.on(BuyBonusPopUpEvents.BUY_POP_UP_CLOSED, this.reportUIState.bind(this, {buyFeatureMenuOpen: false}));

        this.on('General.Flow.PAID_ROUND_ENDED', this.onPaidRoundEnded);
    }

    protected setupInitialSoundConfig(): void {
        const {soundsMuted, soundEnabled} = this._relaxWrapper.getSettings<RubyPlayGameClientWrapperSettings>().getAllProperties();
        const sounds = soundEnabled && !soundsMuted;

        this._send.updateSettings({sounds});
    }

    protected onGameRulesOpened(): void {
        this._isGameRulesOpen = true;
        this.reportUIState({busy: true});
    }

    protected onGameRulesClosed(): void {
        this._isGameRulesOpen = false;
        this.reportUIState({busy: false});
    }

    protected onInfoPageOpened(): void {
        this._isInfoPageOpen = true;
        this.reportUIState({busy: true});
    }

    protected onInfoPageClosed(): void {
        this._isInfoPageOpen = false;
        this.reportUIState({busy: false});
    }

    protected exitGame(): void {
        // TODO: here we can do something before operator forces exiting from the game
    }

    protected freezeGame(): void {
        this._eventManager.emit('General.UI.DISABLE', RelaxEventsHandler.NAME);
        this._eventManager.emitAsync('General.System.PAUSE');
    }

    protected unfreezeGame(): void {
        this._eventManager.emit('General.UI.ENABLE', RelaxEventsHandler.NAME);
        this._eventManager.emitAsync('General.System.RESUME');
    }

    protected stopAutoPlay(): void {
        this._eventManager.emit('Autoplay.FORCE_STOP');
    }

    protected toggleGameRules(): void {
        strictUiEmitter.emit(this._isGameRulesOpen ? 'UI.menu.closed' : 'UI.menu.gameRulesClicked');
    }

    protected togglePaytable(): void {
        strictUiEmitter.emit(this._isInfoPageOpen ? 'UI.menu.closed' : 'UI.menu.infoPageClicked');
    }

    protected updateSettings(settings: IGameSettings): void {
        const {sounds, fastPlay} = settings;

        if (sounds !== undefined) {
            strictUiEmitter.offByOwner('UI.sounds.switchedTo', this);
            strictUiEmitter.emit(sounds ? 'UI.sounds.enable' : 'UI.sounds.disable');
            strictUiEmitter.on('UI.sounds.switchedTo', this.onUpdateSoundSettings.bind(this), {owner: this});
        }

        if (fastPlay !== undefined) {
            this._eventManager.emit(KeypadControlsEvents.TOGGLE_TURBO_BUTTON, fastPlay);
        }
    }

    protected refreshBalanceRequest(): void {
        this._relaxWrapper.refreshBalanceRequest();
    }

    protected onGameLoadStarted(): void {
        this._send.gameLoadStarted();
    }

    protected onGameLoading(id: string | symbol, progress: number): void {
        this._send.gameLoadProgress(progress * 100);
    }

    protected onGameLoadCompleted(): void {
        this._send.gameLoadCompleted();
    }

    protected onAutoPlayStarted(): void {
        this._send.autoPlayStarted();
    }

    protected onAutoPlayFinished(): void {
        this._send.autoPlayFinished();
    }

    protected onBalanceUpdate(id: string | symbol, balance: number): void {
        this._send.balanceUpdate(balance);
    }

    protected onPaidRoundStarted(id: string | symbol, bet: number, isBuyFeature?: boolean): void {
        if (bet) {
            this._paidRoundStated = true;

            if (isBuyFeature) {
                this._send.betUpdate(bet);
            }

            const balanceBefore = this.getPlayerBalance();
            const balance = balanceBefore - bet;

            this._send.roundStarted({balance});
        }
    }

    protected onPaidRoundEnded(id: string | symbol, data: RoundInfo): void {
        if (this._paidRoundStated) {
            this._paidRoundStated = false;

            const wager = this._relaxWrapper.getWager();
            const bet = wager * this._relaxWrapper.getDefaultBet();
            const win = {win: data.totalWin};
            const balance = this.getPlayerBalance();

            this._send.betUpdate(bet);
            this._send.roundFinished({win, balance});
        }
    }

    protected getPlayerBalance(): number {
        return this._relaxWrapper.getSession().getPlayer().getBalance();
    }

    protected onUpdateBet(id: string | symbol, betIndex: number): void {
        this._send.betUpdate(this.getTotalBet(betIndex));
    }

    protected getTotalBet(betIndex: number): number {
        const bet = this._relaxWrapper.getBets()[betIndex];
        const wager = this._relaxWrapper.getWager();

        return bet * wager;
    }

    protected onUpdateTurboSettings(id: string | symbol, isOn: boolean): void {
        this._send.updateSettings({fastPlay: isOn});
    }

    protected onUpdateSoundSettings(isOn: boolean): void {
        this._send.updateSettings({sounds: isOn});
    }

    protected onFeatureStarted(): void {
        this._send.featureStarted();
    }

    protected onFeatureFinished(): void {
        this._send.featureFinished();
    }

    protected onGameRestored(): void {
        this._paidRoundStated = true;
    }

    protected reportUIState(data: IUIState): void {
        this._send.reportUIState(data);
    }

    public onExitGame(): boolean {
        return this._send.exitGame();
    }

    public setupPlayerCurrency(currency: string = RelaxEventsHandler.DEFAULT_CURRENCY): void {
        configure({
            p2pConfig: {currency}
        });
    }

    public get configuration(): IApiConfig {
        return this._configuration;
    }
}
